import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add,
  AccountBalance,
  Visibility,
  MoreVert,
  Block,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  AttachMoney,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useBanking } from '../context/BankingContext';
import { useNavigate } from 'react-router-dom';

const Accounts: React.FC = () => {
  const { user } = useAuth();
  const { accounts, createAccount, deposit, blockAccount, unblockAccount } = useBanking();
  const navigate = useNavigate();
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<'checking' | 'savings'>('checking');
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [depositAmount, setDepositAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAccountId, setMenuAccountId] = useState<string>('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, accountId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuAccountId(accountId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAccountId('');
  };

  const handleCreateAccount = async () => {
    setLoading(true);
    setError('');
    
    try {
      await createAccount(selectedAccountType);
      setSuccess(`${selectedAccountType.charAt(0).toUpperCase() + selectedAccountType.slice(1)} account created successfully!`);
      setCreateDialogOpen(false);
    } catch (err) {
      setError('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    // Enhanced validation
    if (!selectedAccount) {
      setError('Please select an account');
      return;
    }
    
    if (!depositAmount || depositAmount.trim() === '') {
      setError('Please enter a deposit amount');
      return;
    }
    
    const amount = parseFloat(depositAmount);
    if (isNaN(amount)) {
      setError('Please enter a valid number');
      return;
    }
    
    if (amount <= 0) {
      setError('Deposit amount must be greater than zero');
      return;
    }
    
    if (amount > 1000000) {
      setError('Deposit amount cannot exceed $1,000,000');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await deposit(selectedAccount, amount);
      setSuccess(`$${amount.toFixed(2)} deposited successfully!`);
      setDepositDialogOpen(false);
      setDepositAmount('');
      setSelectedAccount('');
    } catch (err) {
      // Display the actual error message from the banking context
      const errorMessage = err instanceof Error ? err.message : 'Failed to deposit funds';
      setError(errorMessage);
      console.error('Deposit error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockAccount = async (accountId: string) => {
    setLoading(true);
    try {
      await blockAccount(accountId);
      setSuccess('Account blocked successfully');
      handleMenuClose();
    } catch (err) {
      setError('Failed to block account');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockAccount = async (accountId: string) => {
    setLoading(true);
    try {
      await unblockAccount(accountId);
      setSuccess('Account unblocked successfully');
      handleMenuClose();
    } catch (err) {
      setError('Failed to unblock account');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAccount = (accountId: string) => {
    navigate(`/accounts/${accountId}`);
    handleMenuClose();
  };

  const totalBalance = accounts.reduce((sum, account) => sum + account.balance, 0);
  const activeAccounts = accounts.filter(account => account.status === 'active').length;
  const blockedAccounts = accounts.filter(account => account.status === 'blocked').length;

  const getAccountIcon = (accountType: string) => {
    switch (accountType) {
      case 'checking':
        return <AttachMoney color="primary" />;
      case 'savings':
        return <TrendingUp color="success" />;
      case 'loan':
        return <TrendingDown color="warning" />;
      default:
        return <AccountBalance color="primary" />;
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'error';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          My Accounts
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={loading}
        >
          New Account
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Account Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalance color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Balance</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              ${totalBalance.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 180px', minWidth: '180px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <Typography variant="h6">Active</Typography>
            </Box>
            <Typography variant="h4" color="success">
              {activeAccounts}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active account{activeAccounts !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 180px', minWidth: '180px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Block color="error" sx={{ mr: 1 }} />
              <Typography variant="h6">Blocked</Typography>
            </Box>
            <Typography variant="h4" color="error">
              {blockedAccounts}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Blocked account{blockedAccounts !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Accounts List */}
      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        Account Details
      </Typography>

      {accounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AccountBalance sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No accounts found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first account to get started with banking
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Account
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {accounts.map((account) => (
            <Card key={account.id} elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box sx={{ mr: 2 }}>
                      {getAccountIcon(account.accountType)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {account.accountType.charAt(0).toUpperCase() + account.accountType.slice(1)} Account
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Account Number: ****{account.accountNumber.slice(-4)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Opened: {account.createdAt.toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h5" color={account.status === 'active' ? 'primary' : 'error'}>
                        ${account.balance.toFixed(2)}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: account.status === 'active' ? 'success.main' : 'error.main',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      >
                        {account.status}
                      </Typography>
                    </Box>
                    
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, account.id)}
                      disabled={loading}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleViewAccount(account.id)}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => {
                      setSelectedAccount(account.id);
                      setDepositDialogOpen(true);
                    }}
                    disabled={account.status === 'blocked'}
                  >
                    Deposit
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewAccount(menuAccountId)}>
          <Visibility sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setSelectedAccount(menuAccountId);
            setDepositDialogOpen(true);
            handleMenuClose();
          }}
        >
          <Add sx={{ mr: 1 }} />
          Deposit Funds
        </MenuItem>
        <Divider />
        {user?.role === 'admin' && (
          <>
            <MenuItem 
              onClick={() => {
                const account = accounts.find(a => a.id === menuAccountId);
                if (account?.status === 'active') {
                  handleBlockAccount(menuAccountId);
                } else {
                  handleUnblockAccount(menuAccountId);
                }
              }}
            >
              {accounts.find(a => a.id === menuAccountId)?.status === 'active' ? (
                <>
                  <Block sx={{ mr: 1 }} />
                  Block Account
                </>
              ) : (
                <>
                  <CheckCircle sx={{ mr: 1 }} />
                  Unblock Account
                </>
              )}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create Account Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Create New Account</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Choose the type of account you want to create:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Card 
              sx={{ 
                cursor: 'pointer', 
                border: selectedAccountType === 'checking' ? 2 : 1,
                borderColor: selectedAccountType === 'checking' ? 'primary.main' : 'divider'
              }}
              onClick={() => setSelectedAccountType('checking')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Checking Account</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Perfect for daily transactions, bill payments, and easy access to your money.
                </Typography>
              </CardContent>
            </Card>
            
            <Card 
              sx={{ 
                cursor: 'pointer', 
                border: selectedAccountType === 'savings' ? 2 : 1,
                borderColor: selectedAccountType === 'savings' ? 'primary.main' : 'divider'
              }}
              onClick={() => setSelectedAccountType('savings')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUp color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Savings Account</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Ideal for saving money and earning interest on your deposits over time.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleCreateAccount} variant="contained" disabled={loading}>
            Create Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deposit Dialog */}
      <Dialog 
        open={depositDialogOpen} 
        onClose={() => setDepositDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Deposit Funds</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter the amount you want to deposit:
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Deposit Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
            }}
            inputProps={{
              min: 0.01,
              max: 1000000,
              step: 0.01,
            }}
            helperText="Enter amount between $0.01 and $1,000,000"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDepositDialogOpen(false);
              setDepositAmount('');
              setSelectedAccount('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleDeposit} variant="contained" disabled={loading}>
            Deposit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Accounts; 